import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChild,
  contentChildren,
  input,
  model,
  output,
} from '@angular/core';

import type { EsnTableColumn, EsnTableInset, EsnTableSort } from '../../../shared/types/data';
import { IconComponent } from '../../atoms/icon/icon.component';
import { TableCellDirective } from './table-cell.directive';
import { TableEmptyDirective } from './table-empty.directive';
import { TableRowDetailDirective } from './table-row-detail.directive';

const INTERACTIVE = 'button, a[href], input, select, textarea, [role="menu"]';

/**
 * Fixed columns keep their Figma width; fill columns share the rest, never under their minimum.
 */
function columnStyle(column: EsnTableColumn): Record<string, string> {
  return column.width != null
    ? { flex: `0 0 ${column.width}px`, width: `${column.width}px` }
    : { flex: '1 0 0px', 'min-width': `${column.minWidth ?? 0}px` };
}

function compareValues(a: unknown, b: unknown): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (a instanceof Date && b instanceof Date) return a.getTime() - b.getTime();
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

/**
 * The console's list view: a header over free-floating frosted rows (Figma Table
 * Header / Table Row), with sortable columns, selectable and expandable rows,
 * and empty and loading states — cells are rendered by the page's own templates.
 */
@Component({
  selector: 'esn-data-table',
  imports: [NgTemplateOutlet, IconComponent],
  templateUrl: './data-table.component.html',
  styleUrl: './data-table.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DataTableComponent<T extends object = Record<string, unknown>> {
  readonly columns = input.required<readonly EsnTableColumn[]>();
  readonly rows = input<readonly T[]>([]);

  /**
   * Row property that identifies a row for selection and expansion.
   */
  readonly rowKey = input<string>('id');

  readonly inset = input<EsnTableInset>(16);

  /**
   * Content context: every cell carries 16px padding instead of the row carrying the inset.
   */
  readonly cellPadding = input(false);

  /**
   * Fixed row height (Content 60, Client Accounts 68); `null` sizes rows to content with 12px above and below.
   */
  readonly rowHeight = input<number | null>(null);

  readonly sort = model<EsnTableSort | null>(null);

  /**
   * Leave the row order alone when sorting — the page sorts (e.g. server-side) on `sort` changes.
   */
  readonly manualSort = input(false);

  readonly selectable = input(false);
  readonly selection = model<readonly unknown[]>([]);

  readonly expandable = input(false);
  readonly expanded = model<readonly unknown[]>([]);

  /**
   * Rows react to hover and click/Enter and report it through `rowClick`.
   */
  readonly clickableRows = input(false);

  readonly loading = input(false);
  readonly loadingRows = input(3);

  readonly emptyText = input('Nothing here yet');
  readonly ariaLabel = input<string | null>(null);
  readonly selectAllLabel = input('Select all rows');
  readonly selectRowLabel = input('Select row');
  readonly expandLabel = input('Show details');

  readonly rowClick = output<T>();

  protected readonly cellTemplates = contentChildren(TableCellDirective);
  protected readonly detailTemplate = contentChild(TableRowDetailDirective);
  protected readonly emptyTemplate = contentChild(TableEmptyDirective);

  protected readonly templateByKey = computed(() => {
    const map = new Map<string, TableCellDirective['template']>();
    for (const dir of this.cellTemplates()) map.set(dir.key(), dir.template);
    return map;
  });

  protected readonly styles = computed(() => this.columns().map(columnStyle));

  protected readonly viewRows = computed(() => {
    const rows = this.rows();
    const sort = this.sort();
    if (!sort || this.manualSort()) return rows;
    const factor = sort.direction === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => factor * compareValues(this.valueOf(a, sort.key), this.valueOf(b, sort.key)));
  });

  protected readonly skeletons = computed(() => Array.from({ length: this.loadingRows() }, (_, i) => i));

  protected readonly selectedSet = computed(() => new Set(this.selection()));
  protected readonly expandedSet = computed(() => new Set(this.expanded()));

  protected readonly allSelected = computed(() => {
    const rows = this.rows();
    const selected = this.selectedSet();
    return rows.length > 0 && rows.every((row) => selected.has(this.keyOf(row)));
  });

  protected valueOf(row: T, key: string): unknown {
    return (row as Record<string, unknown>)[key];
  }

  protected keyOf(row: T): unknown {
    return (row as Record<string, unknown>)[this.rowKey()] ?? row;
  }

  protected ariaSort(column: EsnTableColumn): 'ascending' | 'descending' | 'none' | null {
    if (!column.sortable) return null;
    const sort = this.sort();
    if (sort?.key !== column.key) return 'none';
    return sort.direction === 'asc' ? 'ascending' : 'descending';
  }

  /**
   * Header click cycles the column: ascending → descending → unsorted.
   */
  protected toggleSort(column: EsnTableColumn): void {
    const sort = this.sort();
    if (sort?.key !== column.key) this.sort.set({ key: column.key, direction: 'asc' });
    else if (sort.direction === 'asc') this.sort.set({ key: column.key, direction: 'desc' });
    else this.sort.set(null);
  }

  protected toggleRow(row: T, event: Event): void {
    event.stopPropagation();
    const key = this.keyOf(row);
    const current = this.selection();
    this.selection.set(current.includes(key) ? current.filter((k) => k !== key) : [...current, key]);
  }

  protected toggleAll(): void {
    this.selection.set(this.allSelected() ? [] : this.rows().map((row) => this.keyOf(row)));
  }

  protected toggleExpanded(row: T, event: Event): void {
    event.stopPropagation();
    const key = this.keyOf(row);
    const current = this.expanded();
    this.expanded.set(current.includes(key) ? current.filter((k) => k !== key) : [...current, key]);
  }

  protected activate(row: T, event: Event): void {
    if (!this.clickableRows()) return;

    // Buttons, links and menus inside a cell (actions menu, checkbox) keep their own click.
    const target = event.target as Element | null;
    if (target !== event.currentTarget && target?.closest?.(INTERACTIVE)) return;
    if (event instanceof KeyboardEvent) {
      if (event.target !== event.currentTarget) return;
      event.preventDefault();
    }
    this.rowClick.emit(row);
  }
}
